// account.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, map, tap, throwError } from 'rxjs';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { ISession, IMiddlewareResponse } from '../interfaces/session.interface';
import { ILogin } from '../interfaces/login.interface';
import { environment } from 'src/environments/environment';
import { withJsonHeaders } from '../http/http-options.util';
import { EncryptionHelper } from '../helper/encryption.helper';
import { CapacitorHttpHelper } from '../helper/capacitor-http.helper';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  
  // BehaviorSubject para la sesión actual
  private sessionSubject = new BehaviorSubject<ISession | null>(null);
  session$ = this.sessionSubject.asObservable();
  
  private API_URL = environment.apiUrl;
  
  constructor() {}

  /** Realizar login y actualizar la sesión 
   * @param credentials Objeto con email y password
   * @return Observable de la sesión
  */
  login(login: ILogin) {

    // Añadir ApiKey al body
    login.ApiKey = environment.apiKey;

    // Usamos el patrón de opciones de CapacitorHttp - usar el nuevo endpoint mobile-login
    const options = withJsonHeaders({ url: `${this.API_URL}auth/mobile-login`, data: login });

    return from(CapacitorHttpHelper.post(options)).pipe(
      map((res) => {
        // La respuesta ahora es un MiddlewareResponse
        const middlewareResponse = res.data;
        if (!middlewareResponse.success) {
          throw new Error(middlewareResponse.response || 'Error en el login');
        }
        return middlewareResponse.response;
      }),
      tap((response) => {
        if (response && response.user && response.token) {
          // Convertir de PascalCase a camelCase
          const backendUser = response.user;
          const session: ISession = {
            idUser: backendUser.IdUser,
            name: backendUser.Name,
            rol: backendUser.Rol,
            locationId: backendUser.LocationId,
            locationCode: backendUser.LocationCode,
            locationDescription: backendUser.LocationDescription,
            lowerRange: backendUser.LowerRange,
            upperRange: backendUser.UpperRange,
            workshops: backendUser.Workshops,
            workshopsList: backendUser.WorkshopsList?.map((w: any) => ({
              idLocation: w.IdLocation,
              name: w.Name
            })) || [],
            listAccess: backendUser.ListAccess
          };
          
          // Actualizar la sesión en el BehaviorSubject
          this.sessionSubject.next(session);
          
          // Guardar la sesión en localStorage
          localStorage.setItem('session', JSON.stringify(session));
          
          // Guardar el token JWT
          localStorage.setItem('auth_token', response.token);
          
          // Guardar información adicional
          localStorage.setItem('token_expires_in', response.expiresIn.toString());
          
          // Guardar permisos
          if (response.permissions) {
            localStorage.setItem('user_permissions', JSON.stringify(response.permissions));
          }
          
          // Guardar hash de la sesión para verificación de integridad
          const hash = EncryptionHelper.createHash(session);
          localStorage.setItem('session_hash', hash);
          
          // Crear y guardar timestamp encriptado
          EncryptionHelper.createSessionTimestamp();
        }
      }),
      map((response) => {
        // Convertir de PascalCase a camelCase para el retorno
        const backendUser = response.user;
        return {
          idUser: backendUser.IdUser,
          name: backendUser.Name,
          rol: backendUser.Rol,
          locationId: backendUser.LocationId,
          locationCode: backendUser.LocationCode,
          locationDescription: backendUser.LocationDescription,
          lowerRange: backendUser.LowerRange,
          upperRange: backendUser.UpperRange,
          workshops: backendUser.Workshops,
          workshopsList: backendUser.WorkshopsList?.map((w: any) => ({
            idLocation: w.IdLocation,
            name: w.Name
          })) || [],
          listAccess: backendUser.ListAccess
        } as ISession;
      })
    );
  }

  /**
   * Obtener el token JWT almacenado
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Obtener los permisos del usuario
   */
  getPermissions(): string[] {
    const perms = localStorage.getItem('user_permissions');
    if (perms) {
      try {
        return JSON.parse(perms);
      } catch (error) {
        console.error('Error parsing permissions:', error);
        return [];
      }
    }
    return [];
  }

  /**
   * Verificar si el usuario tiene un permiso específico
   * Ahora también verifica si el usuario es admin por el claim idRol en el token
   */
  hasPermission(permission: string): boolean {
    const permissions = this.getPermissions();
    
    // Si tiene el permiso específico
    if (permissions.includes(permission)) {
      return true;
    }
    
    // Verificar si el usuario es admin por el claim idRol en el token
    const token = this.getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // idRol === 1 es admin (backend lo envía como string en el JWT)
        if (Number(payload.idRol) === 1) {
          return true;
        }
      } catch (error) {
        // Si el token no se puede decodificar, no es admin
      }
    }
    return false;
  }

  /**
   * Verificar si el usuario tiene permisos de administrador
   */
  isAdmin(): boolean {
    const token = this.getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return Number(payload.idRol) === 1;
      } catch (error) {
        return false;
      }
    }
    return false;
  }

  /**
   * Verificar si el usuario puede acceder a un controlador específico
   * @param controller Nombre del controlador (ej: 'UserController', 'VehicleController')
   * @param action Acción específica (ej: 'findAll', 'create', 'update', 'delete')
   * @returns boolean
   */
  hasControllerPermission(controller: string, action: string): boolean {
    const permission = `${controller}.${action}`;
    return this.hasPermission(permission);
  }

  /**
   * Verificar si el usuario puede realizar cualquier acción en un controlador
   * @param controller Nombre del controlador
   * @returns boolean
   */
  hasControllerAccess(controller: string): boolean {
    const permissions = this.getPermissions();

    // Si es admin, tiene acceso a todo
    if (this.isAdmin()) {
      return true;
    }

    // Verificar si tiene algún permiso del controlador
    return permissions.some(permission => permission.startsWith(`${controller}.`));
  }

  /**
   * Obtener todas las acciones permitidas para un controlador específico
   * @param controller Nombre del controlador
   * @returns string[] Array de acciones permitidas
   */
  getControllerActions(controller: string): string[] {
    const permissions = this.getPermissions();

    // Si es admin, devolver todas las acciones comunes
    if (this.isAdmin()) {
      return ['findAll', 'create', 'update', 'delete', 'export'];
    }

    // Filtrar permisos del controlador y extraer las acciones
    return permissions
      .filter(permission => permission.startsWith(`${controller}.`))
      .map(permission => permission.split('.')[1])
      .filter(action => action); // Remover undefined
  }

  /**
   * Verificar múltiples permisos de una vez
   * @param permissions Array de permisos a verificar
   * @param requireAll Si true, requiere todos los permisos. Si false, requiere al menos uno
   * @returns boolean
   */
  hasAnyPermission(permissions: string[], requireAll: boolean = false): boolean {
    if (this.isAdmin()) {
      return true;
    }

    if (requireAll) {
      return permissions.every(permission => this.hasPermission(permission));
    } else {
      return permissions.some(permission => this.hasPermission(permission));
    }
  }

  /**
   * Verificar permisos con wildcards para controladores
   * @param pattern Patrón con wildcard (ej: 'UserController.*', '*.findAll')
   * @returns boolean
   */
  hasWildcardPermission(pattern: string): boolean {
    if (this.isAdmin()) {
      return true;
    }

    const permissions = this.getPermissions();
    const [controllerPart, actionPart] = pattern.split('.');

    if (controllerPart === '*' && actionPart) {
      // Verificar si tiene la acción en cualquier controlador
      return permissions.some(permission => permission.endsWith(`.${actionPart}`));
    }

    if (actionPart === '*' && controllerPart) {
      // Verificar si tiene cualquier acción del controlador
      return permissions.some(permission => permission.startsWith(`${controllerPart}.`));
    }

    return this.hasPermission(pattern);
  }

  /**
   * Actualizar permisos (método público para el interceptor)
   * @param permissions Array de permisos a actualizar
   */
  updatePermissions(permissions: string[]): void {
    if (permissions && Array.isArray(permissions)) {
      localStorage.setItem('user_permissions', JSON.stringify(permissions));
    }
  }

  /**
   * Actualizar información del usuario (método público para el interceptor)
   * @param user Información del usuario a actualizar
   */
  updateUser(user: any): void {
    if (user) {
      // Convertir de PascalCase a camelCase si es necesario
      const session: ISession = {
        idUser: user.IdUser || user.idUser,
        name: user.Name || user.name,
        rol: user.Rol || user.rol,
        locationId: user.LocationId || user.locationId,
        locationCode: user.LocationCode || user.locationCode,
        locationDescription: user.LocationDescription || user.locationDescription,
        lowerRange: user.LowerRange || user.lowerRange,
        upperRange: user.UpperRange || user.upperRange,
        workshops: user.Workshops || user.workshops,
        workshopsList: user.WorkshopsList?.map((w: any) => ({
          idLocation: w.IdLocation || w.idLocation,
          name: w.Name || w.name
        })) || user.workshopsList || [],
        listAccess: user.ListAccess || user.listAccess
      };
      
      // Actualizar en BehaviorSubject
      this.sessionSubject.next(session);
      
      // Actualizar en localStorage
      localStorage.setItem('session', JSON.stringify(session));
      
      // Actualizar hash de integridad
      const hash = EncryptionHelper.createHash(session);
      localStorage.setItem('session_hash', hash);
    }
  }

  /** 
   * Cerrar sesión y limpiar la sesión actual 
  */
  logout() {
    this.sessionSubject.next(null);
    // Limpiar todos los datos de localStorage
    localStorage.removeItem('session');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token_expires_in');
    localStorage.removeItem('user_permissions');
    EncryptionHelper.clearSessions();
  }
  /**
   * Obtener la sesión actual
   * @returns La sesión actual o null si no hay sesión
   */
  getSession(): ISession | null {
    // Intentar cargar la sesión desde localStorage si no está en el BehaviorSubject
    if (!this.sessionSubject.value) {
      const stored = localStorage.getItem('session');
      if (stored) {
        try {
          const session = JSON.parse(stored) as ISession;
          this.sessionSubject.next(session);
          return session;
        } catch (error) {
          // Si hay error al parsear, limpiar localStorage
          console.error('Error parsing session from storage:', error);
          this.logout();
          return null;
        }
      }
    }
    return this.sessionSubject.value;
  }

  /**
   * Verifica integridad de la sesión comparando la sesión normal con la hasheada.
   * Si no coinciden o no existen, devuelve false.
   */
  verifySessionIntegrity(): boolean {
    return EncryptionHelper.verifySessionIntegrity();
  }

  /**
   * Verifica si la sesión ha expirado (3 horas)
   */
  isSessionExpired(): boolean {
    return EncryptionHelper.isSessionExpired();
  }

  setSession(session: ISession): void {
    this.sessionSubject.next(session);
    localStorage.setItem('session', JSON.stringify(session));
    const hash = EncryptionHelper.createHash(session);
    localStorage.setItem('session_hash', hash);
  }
}
